import { Box, Flex, Text } from '@chakra-ui/react'
import { COLORS } from '@ds/tokens/legacy/colors'
import { ColorTokens } from '@ds/tokens/legacy/colors'
import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { TEXT_ENUM } from '@/styles/components/text'

import { SidebarSuboptionType } from '../hooks/useSidebarOptions'

type SuboptionsProps = {
	suboptions?: SidebarSuboptionType[]
	isActive?: boolean
	isFullyExpanded?: boolean
	onMouseEnter?: () => void
}

export const SidebarNavItemSuboptions = ({
	suboptions,
	isActive,
	isFullyExpanded,
	onMouseEnter,
}: SuboptionsProps) => {
	const navigate = useNavigate()
	const [shouldRender, setShouldRender] = useState(false)
	const [animationState, setAnimationState] = useState<
		'entering' | 'entered' | 'exiting' | 'exited'
	>('exited')
	const timeoutRef = useRef<NodeJS.Timeout | null>(null)

	const getFullPath = (path: string): string => {
		if (path.startsWith('http') || path.startsWith('https')) {
			return path
		}

		if (path.startsWith('/')) {
			return path
		}

		return `/${path}`
	}

	useEffect(() => {
		if (suboptions?.length && isActive && isFullyExpanded) {
			setShouldRender(true)
			setAnimationState('entering')

			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current)
			}

			timeoutRef.current = setTimeout(() => {
				setAnimationState('entered')
			}, 30)
		} else if (shouldRender) {
			setAnimationState('exiting')

			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current)
			}

			timeoutRef.current = setTimeout(() => {
				setAnimationState('exited')
				setShouldRender(false)
			}, 250)
		}

		if (!isFullyExpanded && shouldRender) {
			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current)
			}
			setAnimationState('exited')
			setShouldRender(false)
		}

		return () => {
			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current)
			}
		}
	}, [suboptions, isActive, isFullyExpanded, shouldRender])

	if (!shouldRender) return null

	const handleClick = (e: React.MouseEvent, navTo: string) => {
		if (e.button === 0 && !e.ctrlKey && !e.metaKey) {
			e.preventDefault()
			navigate(navTo)
		}
	}

	return (
		<Flex
			direction="column"
			pl="22px"
			gap="4px"
			mt="4px"
			cursor="pointer"
			opacity={
				animationState === 'entering' || animationState === 'exiting' ? 0 : 1
			}
			transform={
				animationState === 'entering' || animationState === 'exiting'
					? 'translateY(-8px)'
					: 'translateY(0)'
			}
			height="auto"
			transition="opacity 250ms ease-in-out, transform 250ms ease-in-out"
			onMouseEnter={onMouseEnter}
			userSelect="none"
		>
			{suboptions?.map((suboption) => {
				if (suboption?.hide) return null
				const fullPath = getFullPath(suboption.navTo)
				return (
					<Box
						key={suboption.title}
						as="a"
						href={fullPath}
						onClick={(e) => handleClick(e, suboption.navTo)}
						onMouseEnter={onMouseEnter}
						w="full"
						_hover={{
							textDecoration: 'none',
						}}
						userSelect="none"
					>
						<Flex
							h="32px"
							alignItems="center"
							px="12px"
							borderRadius="10px"
							bg={
								suboption.isActive ? ColorTokens.card_secondary : 'transparent'
							}
							color={
								suboption.isActive ? COLORS.background[2] : COLORS.background[5]
							}
							_hover={{
								bg: ColorTokens.card_secondary,
							}}
							transition="background-color 200ms ease-in-out"
						>
							<Text
								variant={TEXT_ENUM.medium14}
								noOfLines={1}
								userSelect="none"
							>
								{suboption.title}
							</Text>
						</Flex>
					</Box>
				)
			})}
		</Flex>
	)
}
